import qs from 'qs';
import { runSearch, algoliaResultToProductCard } from '@/util/search';
import {
  RawSearchFacets,
  SearchQueryResponse,
  VivrelleQuery
} from '@/types/Search';

import { NextSearchParams } from '@/types/Next';
import { User } from '@/types/User';
import { State, FilterState, FacetState } from './ProductListApp';

/**
 * This method parses facets to be used with State['facetState']
 */
export const parseFacets = (facets: RawSearchFacets): FacetState => {
  const collection: { [key: string]: string[] } = {};

  // If there are no facets, return nothing back
  if (!facets || Object.keys(facets).length === 0) {
    return {};
  }

  // Parse lvl1 categories for main level categories
  if (facets['ep_categories.lvl1']) {
    Object.keys(facets['ep_categories.lvl1']).forEach((name) => {
      const splitName = name.split('>');
      const parsedName = splitName[splitName.length - 1].trim();
      collection[parsedName] = [];
    });
  }

  if (facets['ep_categories.lvl2']) {
    // Parse lvl2 categories for subcategories
    Object.keys(facets['ep_categories.lvl2']).forEach((name) => {
      const splitName = name.split('>');
      const lvl1Category = splitName[1].trim();
      const lvl2Category = splitName[2].trim();

      if (collection[lvl1Category]) {
        collection[lvl1Category].push(lvl2Category);
      }
    });
  }

  // Brand
  const brand = { ...(facets?.ep_brand || {}) };

  // Color
  const color = { ...(facets?.ep_color || {}) };

  // Occasion
  const occasion = { ...(facets?.ep_occasion || {}) };

  // Material
  const material = { ...(facets?.ep_materials || {}) };

  // Influencer's closets
  const influencers = { ...(facets?.ep_influencers || {}) };

  return {
    collection,
    brand,
    color,
    occasion,
    material,
    influencers
  };
};

export const getQueryParamsFromFilterState = (
  filterState: FilterState
): string => {
  let query: NextSearchParams = {};
  const {
    searchQuery,
    collection,
    subscriptionLevel,
    brand,
    color,
    occasion,
    material,
    influencers,
    inStock,
    newArrivals,
    wishlist,
    // hitsCount, Don't need
    pageNo
  } = filterState;

  if (searchQuery) {
    query['query'] = searchQuery;
  }

  if (collection.collectionType[0]) {
    query['collection'] = collection.collectionType[0];
  }

  if (collection.types.length > 0) {
    query['categories'] = collection.types.slice(0);
  }

  if (subscriptionLevel.length > 0) {
    query['subscriptionLevel'] = subscriptionLevel.slice(0);
  }

  if (brand.length > 0) {
    query['brand'] = brand.slice(0);
  }

  if (color.length > 0) {
    query['color'] = color.slice(0);
  }

  if (occasion.length > 0) {
    query['occasion'] = occasion.slice(0);
  }

  if (material.length > 0) {
    query['material'] = material.slice(0);
  }

  if (influencers.length > 0) {
    query['influencers'] = influencers.slice(0);
  }

  if (inStock) {
    query['inStock'] = 'true';
  }

  if (newArrivals) {
    query['newArrivals'] = 'true';
  }

  if (wishlist) {
    query['filters'] = 'my-wishlist';
  }

  if (pageNo > 1) {
    query['page'] = pageNo.toString();
  }

  return qs.stringify(query, { indices: false });
};

/**
 * Query-param keys owned by `getQueryParamsFromFilterState`. The URL sync uses
 * this to preserve any OTHER params (e.g. `builder.preview` for the CMS editor,
 * `utm_*` attribution) instead of clobbering them on a filter/pagination change.
 */
export const FILTER_QUERY_PARAM_KEYS = new Set([
  'query',
  'collection',
  'categories',
  'subscriptionLevel',
  'brand',
  'color',
  'occasion',
  'material',
  'influencers',
  'inStock',
  'newArrivals',
  'filters',
  'page'
]);

/** `decodeURIComponent` that returns the value unchanged instead of throwing. */
const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * Normalizes a list query param (`?brand=A` or `?brand=A&brand=B`) to a string
 * array, decoding each entry.
 *
 * Next already percent-decodes page `searchParams`, so this is a second decode.
 * It is kept so links that were encoded twice upstream (e.g. a CMS or email
 * link with `?occasion=Date%2520Night`) keep resolving, but it must never
 * throw: a value that isn't valid percent-encoding (a literal `%` from
 * `?brand=%25`, or a truncated `?color=%2`) is kept raw rather than crashing
 * the page.
 *
 * Limitation (pre-existing, kept for those double-encoded external links): a
 * value that still contains a valid `%XX` after Next's decode is decoded again,
 * so `50%50 Blend` comes back as `50P Blend`.
 */
export const parseArrayParam = (
  value: string | string[] | undefined
): string[] => {
  if (!value) {
    return [];
  }
  return (Array.isArray(value) ? value : [value]).map(safeDecodeURIComponent);
};

/**
 * This function takes in query parameters from the URL and converts it into
 * the `FilterState` object used in `<ProductListApp>`
 * @param nextSearchParams - search params from using `searchParams` on the page/server level, or `useSearchParams()` on the client side level
 * @param user - current logged in user. `undefined` if no logged in user.
 * @returns
 */
export const getStateFromQueryParams = (
  nextSearchParams: NextSearchParams,
  user?: User
) => {
  // I don't know why this gets imported as a function server-side, but this mirrors
  // ProductListApp's `initialFilterState` variable. Sorry. Cannot explain whyyyyyy.
  // Normally I would import the variable, but we need to keep them separate... For now...
  const initialFilterState: FilterState = {
    searchQuery: '',
    collection: {
      collectionType: [],
      types: []
    },
    subscriptionLevel: [],
    brand: [],
    color: [],
    occasion: [],
    material: [],
    influencers: [],
    inStock: false,
    newArrivals: false,
    wishlist: false,
    hitsCount: process.env.NEXT_PUBLIC_PLP_HITS_COUNT
      ? parseInt(process.env.NEXT_PUBLIC_PLP_HITS_COUNT)
      : 54,
    pageNo: 1
  };

  const newFilterState: FilterState = {
    ...initialFilterState
  };

  newFilterState.searchQuery = nextSearchParams.query as string;

  newFilterState.wishlist = !!(
    user && nextSearchParams.filters === 'my-wishlist'
  );

  // `collection` is single-valued. String() mirrors the coercion
  // decodeURIComponent always applied (a repeated param comma-joins), so the
  // safe fallback returns a string, never the raw array.
  newFilterState.collection.collectionType = nextSearchParams.collection
    ? [safeDecodeURIComponent(String(nextSearchParams.collection))]
    : [];

  newFilterState.collection.types = parseArrayParam(
    nextSearchParams.categories
  );
  newFilterState.brand = parseArrayParam(nextSearchParams.brand);
  newFilterState.color = parseArrayParam(nextSearchParams.color);
  newFilterState.material = parseArrayParam(nextSearchParams.material);
  newFilterState.occasion = parseArrayParam(nextSearchParams.occasion);
  newFilterState.influencers = parseArrayParam(nextSearchParams.influencers);
  newFilterState.subscriptionLevel = parseArrayParam(
    nextSearchParams.subscriptionLevel
  );

  if (nextSearchParams.inStock) {
    newFilterState.inStock = true;
  }

  if (nextSearchParams.newArrivals) {
    newFilterState.newArrivals = true;
  }

  if (nextSearchParams.page) {
    const pageAsInt = parseInt(nextSearchParams.page as string, 10);
    if (!isNaN(pageAsInt)) {
      newFilterState.pageNo = pageAsInt;
    }
  }

  return newFilterState;
};

/** Runs a search using a FilterState object from `<ProductListApp>` */
export const runSearchWithFilterState = async (
  filterState: State['filterState'],
  user?: User
): Promise<SearchQueryResponse> => {
  const vivrelleQuery: VivrelleQuery = { ...filterState };
  if (filterState.wishlist && user) {
    vivrelleQuery.customerId = user.id;
  }

  if (filterState.priveeOnly) {
    vivrelleQuery.limitToSubscriptionLevel = 'Privee';
  }

  vivrelleQuery.pageNo = vivrelleQuery.pageNo
    ? vivrelleQuery.pageNo - 1
    : undefined;

  const response = await runSearch(vivrelleQuery);

  return response;
};

/** Builds out the new state object from a search response on the `<ProductListApp>` component */
export const buildStateFromSearch = (
  searchResponse: SearchQueryResponse,
  filters: FilterState
): State => {
  let titleHeadline = '';

  if (filters.wishlist) {
    titleHeadline = 'My Wishlist';
  }

  // A search query overrides 'my wishlist' for title headline
  if (filters.searchQuery) {
    titleHeadline = 'Search results for';
  }

  return {
    facets: parseFacets(searchResponse.data?.facets),
    filterState: filters,
    filterMenuOpen: false,
    loading: false,
    titleHeadline,
    items: searchResponse.data?.hits?.map(algoliaResultToProductCard) || [],
    totalResults: searchResponse.data?.nbHits || 0,
    totalPages: searchResponse.data?.nbPages || 0,
    totalFilters: getFilterCount(filters)
  };
};

/** Creates a full deep object copy of `FilterState` */
export function createFilterStateCopy(filterState: FilterState): FilterState {
  return {
    ...filterState,
    collection: {
      ...filterState.collection,
      collectionType: [...filterState.collection.collectionType],
      types: [...filterState.collection.types]
    },
    subscriptionLevel: [...filterState.subscriptionLevel],
    brand: [...filterState.brand],
    color: [...filterState.color],
    occasion: [...filterState.occasion],
    material: [...filterState.material],
    influencers: [...filterState.influencers]
  };
}

const getFilterCount = (filterState: State['filterState']): number => {
  const brandCount = filterState.brand.length;
  const colorCount = filterState.color.length;
  const categoryCount = filterState.collection.types.length;
  const influencerCount = filterState.influencers.length;
  const materialCount = filterState.material.length;
  const occasionCount = filterState.occasion.length;
  const subLevelCount = filterState.subscriptionLevel.length;
  const wishlistCount = filterState.wishlist ? 1 : 0;
  const newArrivalCount = filterState.newArrivals ? 1 : 0;

  // For purposes of UX, we don't consider collection type (ex. Handbag, Jewelry, etc)
  // a filter, nor do we want to expose the total tally of these types
  // const collectionTypeCount = filterState.collection.collectionType.length;

  const totalCount =
    brandCount +
    colorCount +
    categoryCount +
    influencerCount +
    materialCount +
    occasionCount +
    subLevelCount +
    wishlistCount +
    newArrivalCount;

  return totalCount;
};

export const filterCountString = (state: State) => {
  const filterCount = state.totalFilters;
  if (filterCount > 0) {
    return `(${filterCount})`;
  }
  return '';
};

/** Initial filter state for Privee PLP with subscriptionLevel locked to Privee */
export const priveeInitialFilterState: FilterState = {
  searchQuery: '',
  collection: {
    collectionType: [],
    types: []
  },
  subscriptionLevel: [],
  brand: [],
  color: [],
  occasion: [],
  material: [],
  influencers: [],
  inStock: false,
  newArrivals: false,
  wishlist: false,
  hitsCount: 24,
  pageNo: 1,
  priveeOnly: true
};
